// Persistence for the home the user last looked at.
//
// Its ONLY consumer is the root landing (PropertyLanding): a bare `/` entry —
// a bookmark, the post-sign-in landing — is sent to this home. Which home an open
// page shows is carried by its URL (/property/:homeId), never by this value, so it is
// a landing default and not shared live state: localStorage is shared across tabs,
// and reading it anywhere else would let a home switch in one tab redirect another.
// It is deliberately independent of the darwin_core store (which is cleared on
// sign-out) and of the Cognito token store — it holds a non-sensitive UI
// preference, so a plain localStorage wrapper is enough.
//
// Every access is guarded: localStorage is absent in non-browser contexts (the
// SSR-less boot smoke, some tests) and throws in Safari private mode / when the
// quota is exceeded. A persistence failure must never break sign-in, so reads
// fall back to null and writes/clears fail silently.

const STORAGE_KEY = 'halo.web.selectedHomeId';

function getStore() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Accessing localStorage can itself throw (e.g. blocked by browser policy).
    return null;
  }
}

// The id of the last selected home, or null if none is stored / storage is
// unavailable.
export function getSelectedHomeId() {
  const store = getStore();
  if (!store) {
    return null;
  }
  try {
    return store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

// Persists the selected home id. A null/empty id clears the stored value instead
// of writing a falsy string. Returns true on success, false if it could not be
// persisted.
export function setSelectedHomeId(homeId) {
  const store = getStore();
  if (!store) {
    return false;
  }
  try {
    if (homeId) {
      store.setItem(STORAGE_KEY, String(homeId));
    } else {
      store.removeItem(STORAGE_KEY);
    }
    return true;
  } catch {
    return false;
  }
}

// Removes any stored selection. Safe to call when nothing is stored.
export function clearSelectedHomeId() {
  const store = getStore();
  if (!store) {
    return;
  }
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Ignore: clearing a preference must never surface an error.
  }
}
